import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { AsyncSearch, SearchableSelect } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import ControlledDataTable from 'components/tables/controlled-data-table';
import { DataTableRef } from 'components/tables/data-table';
import { ControlledDataTableProvider } from 'components/tables/data-table-context';
import { useSelectedLocation } from 'contexts';
import useConfirm from 'hooks/use-confirm';
import useLocationCarrierApi, { LocationCarrier } from 'hooks/use-location-carrier-api';
import { useSearchParams } from 'react-router-dom';
import { getTruckingCompanyById, searchTruckingCompanies } from 'services';
import { Location, Role, TruckingCompany } from 'types';
import { T_DataTableColumn } from 'types/data-table';
import { Filter, FilterOperator, FilterState, LogicalOperator } from 'utils';
import { formatTruckingCompany } from 'utils/format';

class LocationCarrierColumns {
  id: T_DataTableColumn<LocationCarrier, 'number'> = {
    field: 'id',
    label: 'ID',
    type: 'number',
    width: 48
  };
  createdBy: T_DataTableColumn<LocationCarrier, 'text'> = {
    allowedRoles: [Role.ADMIN, Role.BILLING_ADMIN],
    field: 'createdBy',
    label: 'USER',
    type: 'text',
    width: 48
  };
  createdDate: T_DataTableColumn<LocationCarrier, 'dateTime'> = {
    field: 'createdDate',
    dateFormat: 'l',
    label: 'DATE',
    type: 'dateTime',
    width: 48
  };
  startDate: T_DataTableColumn<LocationCarrier, 'dateTime'> = {
    field: 'startDate',
    dateFormat: 'l',
    label: 'START',
    type: 'dateTime',
    width: 48
  };
  location: T_DataTableColumn<LocationCarrier, 'location'> = {
    field: 'location',
    getValueLabel: location => location?.name,
    label: 'LOCATION',
    type: 'location',
    width: 64
  };
  truckingCompany: T_DataTableColumn<LocationCarrier, 'truckingCompany'> = {
    field: 'truckingCompany',
    getValueLabel: truckingCompany => `${truckingCompany?.name ?? 'N/A'} (${truckingCompany?.scacCode ?? 'N/A'})`,
    label: 'CARRIER',
    type: 'truckingCompany',
    width: 64
  };
  gateInAllowed: T_DataTableColumn<LocationCarrier, 'boolean'> = {
    field: 'gateInAllowed',
    getValueLabel: allowed => (allowed ? 'ALLOW' : 'STOP'),
    label: 'GATE-IN',
    type: 'boolean',
    width: 64
  };
  gateOutAllowed: T_DataTableColumn<LocationCarrier, 'boolean'> = {
    field: 'gateOutAllowed',
    getValueLabel: allowed => (allowed ? 'ALLOW' : 'STOP'),
    label: 'GATE-OUT',
    type: 'boolean',
    width: 64
  };
  notes?: T_DataTableColumn<LocationCarrier, 'text'> = {
    field: 'notes',
    label: 'NOTES',
    type: 'text',
    width: 96
  };
}

const LocationRules: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { getConfirmation, ConfirmationDialog } = useConfirm();
  const { deleteRule, fetchData } = useLocationCarrierApi();
  const { locations } = useSelectedLocation();

  const dataTableRef = useRef<DataTableRef>(null);
  const columns = useMemo(() => Object.values(new LocationCarrierColumns()), []);
  const initialColumns = useMemo(() => Object.keys(new LocationCarrierColumns()).slice(1), []);

  const [location, setLocation] = useState<Location>();
  const [truckingCompany, setTruckingCompany] = useState<TruckingCompany | null>();

  useEffect(() => {
    const truckingCompanyId = Number(searchParams.get('carrier'));
    if (truckingCompanyId) {
      getTruckingCompanyById(truckingCompanyId).then(setTruckingCompany);
    }
  }, [searchParams]);

  const filterState: FilterState = useMemo(() => {
    const filterOperator = FilterOperator.EQUALS;
    const filters: Filter[] = [];
    if (truckingCompany) filters.push({ field: 'truckingCompany.id', filterOperator, value: truckingCompany.id });
    if (location) filters.push({ field: 'location.id', filterOperator, value: location.id });
    return { logicalOperator: LogicalOperator.AND, filters };
  }, [location, truckingCompany]);

  const handleDelete = useCallback(
    async (rule: LocationCarrier) => {
      const confirm = await getConfirmation(
        'Delete rule?',
        `Location - ${rule.location?.name}\nCarrier - ${formatTruckingCompany(rule.truckingCompany)}`
      );
      if (confirm) {
        await deleteRule(rule.id);
        dataTableRef.current?.forceFetch();
      }
    },
    [deleteRule, getConfirmation]
  );

  const handleEdit = useCallback(
    (rule?: LocationCarrier | null) => rule?.id && navigate(`/scac-management/location-rules/${rule.id}`),
    [navigate]
  );

  const uid = useRef(0);
  const handleReset = useCallback(() => {
    setLocation(undefined);
    setTruckingCompany(undefined);
    uid.current++;
  }, [setLocation, setTruckingCompany]);

  return (
    <>
      <ConfirmationDialog danger />
      <ErrorDialog />
      <SuccessDialog />
      <ControlledDataTableProvider<LocationCarrier> initialColumns={initialColumns} filterState={filterState}>
        <ControlledDataTable<LocationCarrier>
          columns={columns}
          dataTableRef={dataTableRef}
          fetchData={fetchData}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          tableToolbar
        >
          <div className="grid-3" key={uid.current}>
            <SearchableSelect
              containerClass="text-field"
              inputClass={!location ? 'blank' : ''}
              label="LOCATION"
              onValueSelected={locationId => {
                setLocation(locations.find(({ id }) => id === Number(locationId)));
              }}
              options={locations.map(({ id, name }) => ({ label: name, value: `${id}` }))}
              value={`${location?.id ?? ''}`}
              data-cy="scac-management-location-input"
            />
            <AsyncSearch<TruckingCompany>
              label="CARRIER"
              formatValue={formatTruckingCompany}
              handleSelect={setTruckingCompany}
              search={searchTruckingCompanies}
              value={truckingCompany}
              data-cy="scac-management-carrier-search"
            />
            <div className="align-self-center">
              <button className="btn-close d-flex g-2 align-items-center" onClick={handleReset} title="clear filters">
                <span className={`material-icons icon-large filters-${filterState.filters?.length}`}>
                  filter_alt_off
                </span>
              </button>
            </div>
          </div>
          <button
            className="btn-primary"
            onClick={() => navigate('/scac-management/location-rules/new')}
            data-cy="scac-management-add-location-rule-button"
          >
            ADD LOCATION RULE
          </button>
        </ControlledDataTable>
      </ControlledDataTableProvider>
    </>
  );
};

export default LocationRules;
